import React, { useContext, useState } from "react";
import { Link, NavLink, useLocation } from 'react-router-dom';
import { MdOutlineCancel } from 'react-icons/md';
import { TooltipComponent } from '@syncfusion/ej2-react-popups';
import AppContext from '../contexts/AppContext';
import { links } from '../data/links';

const Sidebar = () => {
  const location = useLocation();
  const { setActiveSectionLinks, setSuppressAutoNavigate, currentColor, activeMenu, setActiveMenu, userData, screenSize, companyProfile } = useContext(AppContext);

  const [openSection, setOpenSection] = useState(null);
  const [activeSectionTitle, setActiveSectionTitle] = useState(null);
  // activeSidebarSection state can be removed if activeSectionTitle is sufficient,
  // but I'm leaving it as is since you kept it in the original.
  // const [activeSidebarSection, setActiveSidebarSection] = useState(null); 

  const userDepartment = (userData?.data?.employees?.departments?.name || '').toLowerCase();
  const userRole = (userData?.data?.user_roles?.name || '').toLowerCase();

  // console.log('links sir ', links); // Good for debugging, but consider removing for production.

  const filteredSections = links
    .map(section => {
      const hasDepartmentAccess = (section.department || []).map(dep => dep.toLowerCase()).includes(userDepartment);
      const hasRoleAccess = (section.role || []).map(role => role.toLowerCase()).includes(userRole);
      const hasSectionAccess = hasDepartmentAccess || hasRoleAccess;

      const filteredLinks = section.links.filter(link => {
        const linkRoles = (link.role || []).map(role => role.toLowerCase());
        return linkRoles.length === 0 || linkRoles.includes(userRole);
      });

      return (hasSectionAccess || filteredLinks.length > 0)
        ? { ...section, links: filteredLinks }
        : null;
    })
    .filter(section => section !== null);

  const handleToggleSection = (section) => {
    const isCurrentlyOpen = openSection === section.title;

    if (!isCurrentlyOpen) {
      setActiveSectionLinks(section.links);
      setSuppressAutoNavigate(false);
      setActiveSectionTitle(section.title);
      // setActiveSidebarSection(section.title); // If you removed the state, remove this line too.
      setOpenSection(section.title);
    } else {
      setOpenSection(null);
    }
  };

  const handleCloseSideBar = () => {
    if (activeMenu !== undefined && screenSize <= 900) {
      setActiveMenu(false);
    }
  };

  const activeLink = 'flex items-center gap-3 pl-3 py-2 rounded-lg text-white text-sm m-1 hover:text-white transition-all duration-300 ease-in-out transform hover:scale-105 bg-indigo-700 shadow-lg';
  const normalLink = 'flex items-center gap-3 pl-3 py-2 rounded-lg text-sm text-gray-300 dark:text-gray-200 dark:hover:text-white hover:bg-gradient-to-r hover:from-indigo-600 hover:to-purple-700 m-1 transition-all duration-300 ease-out hover:scale-105';

  return (
    // The width and ml-2 are untouched, exactly as per your original request.
    <div className={`ml-2 h-screen md:overflow-hidden overflow-auto md:hover:overflow-auto pb-6 
      ${activeMenu
        ? 'bg-gradient-to-br from-[#0a0f24] via-[#131e38] to-[#0a0f24] backdrop-blur-xl shadow-inner border-r border-indigo-900'
        : 'bg-transparent'}`}
    >
      {activeMenu && (
        <>
          <div className="flex justify-between items-center px-4 pt-6">
            <Link to="/" onClick={handleCloseSideBar} className="flex items-center gap-3 text-white text-xl font-extrabold tracking-wide">
              {companyProfile?.photo_url && (
                <img className="h-12 w-12 rounded-full border-2 border-purple-600 shadow-md" src={companyProfile.photo_url} alt="logo" />
              )}
              
              <span>{companyProfile?.name}</span>
            </Link>
            <TooltipComponent content="Close Menu" position="BottomCenter">
              <button
                type="button"
                onClick={() => setActiveMenu(!activeMenu)}
                style={{ color: currentColor }}
                className="text-xl rounded-full p-2 hover:bg-light-gray md:hidden"
              >
                <MdOutlineCancel />
              </button>
            </TooltipComponent>
          </div>

          <div className="mt-6 px-2">
            {filteredSections.map((section) => {
              // Determine if any link in this section is currently active
              const isAnyLinkActiveInSection = section.links.some(link => `/${link.name}` === location.pathname);

              return (
                <div key={section.title}>
                  <div
                    className={`relative flex items-center justify-between px-3 py-2 mt-2 rounded-md cursor-pointer transition-all duration-300 ease-in-out group
                      ${activeSectionTitle === section.title
                        ? 'bg-gradient-to-r from-purple-700 to-indigo-700 text-white shadow-[0_0_15px_rgba(138,43,226,0.6)] scale-105 border-l-4 border-cyan-400'
                        : 'hover:bg-gradient-to-r hover:from-indigo-800 hover:to-purple-800 hover:scale-[1.02]'}`}
                    onClick={() => handleToggleSection(section)}
                  >
                    <div className="flex items-center gap-3 ml-2">
                      <div className={`text-lg transition-all duration-300 ease-in-out
                        ${activeSectionTitle === section.title
                          ? 'text-cyan-300 drop-shadow-[0_0_10px_#00ffff]'
                          : 'text-cyan-400 group-hover:drop-shadow-[0_0_6px_#00ffff]'}`}>
                        {section.icon}
                      </div>
                      <p className={`text-xs uppercase tracking-wider font-semibold transition-all duration-300 ease-in-out
                        ${activeSectionTitle === section.title
                          ? 'text-white drop-shadow-[0_0_3px_#ffffff]'
                          : 'text-gray-300 group-hover:text-gray-100'} flex items-center gap-2`}>
                        {section.title}
                        {/* THE NEW FUTURISTIC POINTER ICON */}
                        {isAnyLinkActiveInSection && (
                          <svg className="w-3 h-3 text-cyan-400 ml-1 drop-shadow-[0_0_5px_#00ffff] animate-pulse" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
                          </svg>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="ml-1">
                    {openSection === section.title && section.links.map((link) => (
                      <NavLink
                        to={`/${link.name}`}
                        key={link.name}
                        onClick={() => {
                          setSuppressAutoNavigate(false);
                          handleCloseSideBar();
                        }}
                        style={({ isActive }) => ({
                          backgroundColor: isActive ? currentColor : '',
                          boxShadow: isActive ? '0 0 10px rgba(255, 255, 255, 0.3)' : '',
                        })}
                        className={({ isActive }) => isActive ? activeLink : normalLink}
                      >
                        <div className="text-cyan-400 drop-shadow-[0_0_5px_#00ffff]">{link.icon}</div>
                        <span className="capitalize text-gray-100">{link.name}</span>
                      </NavLink>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default Sidebar;